import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, Check, Sparkles, Building2, Users,
  MessageSquare, ShoppingBag, FileText, Rocket,
  Loader2, X, ChevronRight
} from 'lucide-react';
import { apiClient } from '../../lib/api';

// ============================================
// CONFIGURAÇÃO DAS ETAPAS
// ============================================

const ONBOARDING_STEPS = [
  {
    id: 1,
    title: 'Sobre o negócio',
    subtitle: 'Vamos conhecer melhor sua marca',
    icon: Building2,
    fields: [
      {
        key: 'brand_name',
        label: 'Qual é o nome da sua marca ou negócio?',
        type: 'text',
        placeholder: 'Ex: Loja da Maria',
        maxLength: 100,
        required: true
      },
      {
        key: 'business_type',
        label: 'Você é:',
        type: 'radio',
        options: [
          { value: 'criador_marca', label: 'Criador de conteúdo / Marca pessoal' },
          { value: 'empresa', label: 'Empresa' }
        ],
        required: true
      },
      {
        key: 'product_type',
        label: 'O que você vende principalmente?',
        type: 'radio',
        options: [
          { value: 'produto', label: 'Produto' },
          { value: 'servico', label: 'Serviço' },
          { value: 'conteudo', label: 'Conteúdo' }
        ],
        required: true
      }
    ]
  },
  {
    id: 2,
    title: 'Público',
    subtitle: 'Quem você quer alcançar?',
    icon: Users,
    fields: [
      {
        key: 'target_audience',
        label: 'Quem é seu público?',
        type: 'radio',
        options: [
          { value: 'pessoas_fisicas', label: 'Pessoas físicas' },
          { value: 'empresas', label: 'Empresas' },
          { value: 'ambos', label: 'Ambos' }
        ],
        required: true
      },
      {
        key: 'age_range',
        label: 'Faixa etária principal:',
        type: 'radio',
        options: [
          { value: '18_25', label: '18–25' },
          { value: '26_35', label: '26–35' },
          { value: '36_mais', label: '36+' }
        ],
        required: true
      },
      {
        key: 'purchase_motivation',
        label: 'O que mais faz seu público comprar?',
        type: 'radio',
        options: [
          { value: 'preco', label: 'Preço' },
          { value: 'confianca', label: 'Confiança' },
          { value: 'resultado', label: 'Resultado' },
          { value: 'autoridade', label: 'Autoridade' }
        ],
        required: true
      }
    ]
  },
  {
    id: 3,
    title: 'Comunicação',
    subtitle: 'Como você quer se comunicar?',
    icon: MessageSquare,
    fields: [
      {
        key: 'tone',
        label: 'Tom de voz:',
        type: 'radio',
        options: [
          { value: 'profissional', label: 'Profissional' },
          { value: 'amigavel', label: 'Amigável' },
          { value: 'direto', label: 'Direto e persuasivo' }
        ],
        required: true
      },
      {
        key: 'communication_style',
        label: 'Estilo da comunicação:',
        type: 'radio',
        options: [
          { value: 'formal', label: 'Formal' },
          { value: 'informal', label: 'Informal' },
          { value: 'equilibrado', label: 'Equilibrado' }
        ],
        required: true
      },
      {
        key: 'emoji_usage',
        label: 'Uso de emojis:',
        type: 'radio',
        options: [
          { value: 'sim', label: 'Sim' },
          { value: 'pouco', label: 'Pouco' },
          { value: 'nao', label: 'Não' }
        ],
        required: true
      }
    ]
  },
  {
    id: 4,
    title: 'Conteúdo',
    subtitle: 'O que você quer publicar?',
    icon: FileText,
    fields: [
      {
        key: 'main_goal',
        label: 'Principal objetivo:',
        type: 'radio',
        options: [
          { value: 'vender', label: 'Vender' },
          { value: 'crescer_seguidores', label: 'Crescer seguidores' },
          { value: 'engajar', label: 'Engajar' },
          { value: 'autoridade', label: 'Autoridade' }
        ],
        required: true
      },
      {
        key: 'content_themes',
        label: 'Temas principais (múltipla escolha):',
        type: 'checkbox',
        options: [
          { value: 'dicas', label: 'Dicas' },
          { value: 'bastidores', label: 'Bastidores' },
          { value: 'vendas', label: 'Vendas' },
          { value: 'prova_social', label: 'Prova social' }
        ],
        required: true
      },
      {
        key: 'content_format',
        label: 'Formato preferido:',
        type: 'radio',
        options: [
          { value: 'videos', label: 'Vídeos (Reels/Shorts)' },
          { value: 'carrossel', label: 'Carrossel' },
          { value: 'misto', label: 'Misto' }
        ],
        required: true
      }
    ]
  },
  {
    id: 5,
    title: 'Vendas',
    subtitle: 'Sua estratégia comercial',
    icon: ShoppingBag,
    fields: [
      {
        key: 'sells_online',
        label: 'Você já vende online?',
        type: 'radio',
        options: [
          { value: 'sim', label: 'Sim' },
          { value: 'nao', label: 'Não' }
        ],
        required: true
      },
      {
        key: 'contact_channel',
        label: 'Principal canal:',
        type: 'radio',
        options: [
          { value: 'whatsapp', label: 'WhatsApp' },
          { value: 'direct', label: 'Direct' },
          { value: 'site_link', label: 'Site / Link' }
        ],
        required: true
      },
      {
        key: 'average_ticket',
        label: 'Ticket médio:',
        type: 'radio',
        options: [
          { value: 'ate_100', label: 'Até R$100' },
          { value: '100_500', label: 'R$100–500' },
          { value: '500_mais', label: 'R$500+' }
        ],
        required: true
      }
    ]
  },
  {
    id: 6,
    title: 'Finalização',
    subtitle: 'Último detalhe',
    icon: Rocket,
    fields: [
      {
        key: 'marketing_level',
        label: 'Seu nível com marketing:',
        type: 'radio',
        options: [
          { value: 'iniciante', label: 'Iniciante' },
          { value: 'intermediario', label: 'Intermediário' },
          { value: 'avancado', label: 'Avançado' }
        ],
        required: true
      }
    ]
  }
];

// ============================================
// COMPONENTES AUXILIARES
// ============================================

const ProgressBar = ({ currentStep, totalSteps }) => {
  const percentage = ((currentStep) / totalSteps) * 100;
  
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600">
          Etapa {currentStep} de {totalSteps}
        </span>
        <span className="text-sm font-semibold text-amber-600">
          {Math.round(percentage)}%
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const RadioOption = ({ option, selected, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(option.value)}
    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
      selected === option.value
        ? 'border-amber-500 bg-amber-50 shadow-md'
        : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected === option.value
          ? 'border-amber-500 bg-amber-500'
          : 'border-gray-300'
      }`}>
        {selected === option.value && (
          <Check className="w-3 h-3 text-white" />
        )}
      </div>
      <span className={`font-medium ${
        selected === option.value ? 'text-gray-900' : 'text-gray-700'
      }`}>
        {option.label}
      </span>
    </div>
  </button>
);

const CheckboxOption = ({ option, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(option.value)}
    className={`p-4 rounded-xl border-2 text-left transition-all ${
      checked
        ? 'border-amber-500 bg-amber-50 shadow-md'
        : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
        checked
          ? 'border-amber-500 bg-amber-500'
          : 'border-gray-300'
      }`}>
        {checked && (
          <Check className="w-3 h-3 text-white" />
        )}
      </div>
      <span className={`font-medium ${
        checked ? 'text-gray-900' : 'text-gray-700'
      }`}>
        {option.label}
      </span>
    </div>
  </button>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const OnboardingWizard = () => {
  const navigate = useNavigate();
  
  const [searchParams] = useSearchParams();
  const isRedo = searchParams.get('redo') === 'true';

  const [currentStep, setCurrentStep] = useState(1);
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Carregar status inicial do onboarding
  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      setIsLoading(true);

      // 🆕 Se é redo, resetar no backend e começar do zero
      if (isRedo) {
        try {
          await apiClient.post('/api/onboarding/reset');
        } catch (resetErr) {
          console.warn('Erro ao resetar onboarding:', resetErr);
        }
        setCurrentStep(1);
        setResponses({});
        setIsLoading(false);
        return;
      }

      const response = await apiClient.get('/api/onboarding/status');
      const data = response.data;
      
      // Se já completou, redirecionar
      if (data.completed) {
        navigate('/contents');
        return;
      }
      
      // Restaurar respostas salvas
      if (data.responses) {
        const flatResponses = {};
        Object.entries(data.responses).forEach(([stepKey, stepData]) => {
          Object.entries(stepData || {}).forEach(([key, value]) => {
            if (value !== null) {
              flatResponses[key] = value;
            }
          });
        });
        setResponses(flatResponses);
      }
      
      // Ir para a etapa atual
      setCurrentStep(data.current_step || 1);
      
    } catch (err) {
      console.error('Erro ao carregar onboarding:', err);
      setError('Erro ao carregar suas preferências');
    } finally {
      setIsLoading(false);
    }
  };

  // Salvar etapa atual
  const saveCurrentStep = async () => {
    const step = ONBOARDING_STEPS[currentStep - 1];
    const stepResponses = {};
    
    step.fields.forEach(field => {
      if (responses[field.key] !== undefined) {
        stepResponses[field.key] = responses[field.key];
      }
    });
    
    try {
      setIsSaving(true);
      await apiClient.post('/api/onboarding/step', {
        step: currentStep,
        responses: stepResponses
      });
    } catch (err) {
      console.error('Erro ao salvar etapa:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Validar etapa atual
  const isStepValid = () => {
    const step = ONBOARDING_STEPS[currentStep - 1];
    
    return step.fields.every(field => {
      if (!field.required) return true;
      
      const value = responses[field.key];
      
      if (field.type === 'checkbox') {
        return Array.isArray(value) && value.length > 0;
      }
      
      return value !== undefined && value !== null && value !== '';
    });
  };

  // Handlers
  const handleInputChange = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const handleCheckboxChange = (key, value) => {
    setResponses(prev => {
      const current = prev[key] || [];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [key]: newValues };
    });
  };

  const handleNext = async () => {
    await saveCurrentStep();
    
    if (currentStep < ONBOARDING_STEPS.length) {
      setCurrentStep(currentStep + 1);
      // Scroll para o topo imediatamente
      window.scrollTo(0, 0);
    } else {
      // Finalizar
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll para o topo imediatamente
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = async () => {
    try {
      setIsSaving(true);
      await apiClient.post('/api/onboarding/complete');
      navigate('/contents');
    } catch (err) {
      console.error('Erro ao finalizar onboarding:', err);
      setError('Erro ao finalizar. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      setIsSaving(true);
      await apiClient.post('/api/onboarding/skip');
      navigate('/contents');
    } catch (err) {
      console.error('Erro ao pular onboarding:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  const step = ONBOARDING_STEPS[currentStep - 1];
  const StepIcon = step.icon;
  const isLastStep = currentStep === ONBOARDING_STEPS.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header com Progress Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Progress Bar - ocupa maior parte */}
            <div className="flex-1">
              <ProgressBar 
                currentStep={currentStep} 
                totalSteps={ONBOARDING_STEPS.length} 
              />
            </div>
            
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-32 px-4">
        <div className="max-w-2xl mx-auto">

          {/* Step Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl shadow-lg mb-4">
              <StepIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {step.title}
            </h1>
            <p className="text-gray-600">
              {step.subtitle}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Questions */}
          <div className="space-y-8">
            {step.fields.map((field, fieldIndex) => (
              <div key={field.key} className="animate-fadeIn" style={{ animationDelay: `${fieldIndex * 100}ms` }}>
                <label className="block text-lg font-semibold text-gray-900 mb-4">
                  {field.label}
                  {!field.required && (
                    <span className="text-sm font-normal text-gray-400 ml-2">(opcional)</span>
                  )}
                </label>

                {/* Text Input */}
                {field.type === 'text' && (
                  <input
                    type="text"
                    value={responses[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-amber-100 focus:border-amber-500 transition-all"
                  />
                )}

                {/* Radio Options */}
                {field.type === 'radio' && (
                  <div className="space-y-3">
                    {field.options.map(option => (
                      <RadioOption
                        key={option.value}
                        option={option}
                        selected={responses[field.key]}
                        onChange={(value) => handleInputChange(field.key, value)}
                      />
                    ))}
                  </div>
                )}

                {/* Checkbox Options */}
                {field.type === 'checkbox' && (
                  <div className="grid grid-cols-2 gap-3">
                    {field.options.map(option => (
                      <CheckboxOption
                        key={option.value}
                        option={option}
                        checked={(responses[field.key] || []).includes(option.value)}
                        onChange={(value) => handleCheckboxChange(field.key, value)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer Navigation */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-100 py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          {/* Back Button */}
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-3 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Voltar
              </button>
            )}
            <button
              onClick={handleSkip}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 text-sm font-medium transition-colors"
            >
              Responder depois
            </button>
          </div>

          {/* Next/Finish Button */}
          <button
            onClick={handleNext}
            disabled={!isStepValid() || isSaving}
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-500 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Salvando...
              </>
            ) : isLastStep ? (
              <>
                Finalizar
                <Sparkles className="w-5 h-5" />
              </>
            ) : (
              <>
                Continuar
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </footer>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default OnboardingWizard;